import React, {useEffect, useState} from "react";
import "../global.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ProductHome from "../common/ProductHome";
import GradientButtons from "../common/GradientButtons";
import Counter from "../common/Counter";
import { Helmet } from "react-helmet-async";
import Papa from "papaparse";
import { NewsItem } from "../common/news/types";
import { filterPublic } from "../common/news/comingSoonhelper";

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    if (window.location.hash) {
      let newPath = window.location.pathname + window.location.search;
      if (newPath.endsWith("/")) newPath = newPath.slice(0, -1);
      window.history.pushState("", document.title, newPath);
    }
    window.onload = () => window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      const response = await fetch("/csv/news_content.csv");
      const csvText = await response.text();
      Papa.parse(csvText, {
        header: true,
        complete: (result) => {
          const visibleNews = filterPublic(result.data as NewsItem[]);
          const sorted = visibleNews.sort(
              (a, b) => new Date(b.publication_date).getTime() - new Date(a.publication_date).getTime()
          );
          setLatestNews(sorted.slice(0, 3));
        },
      });
    };
    fetchNews();
  }, []);

  const events = [

    {
      logo: "/IMAGES/MEETS_LOGOS/IAC 2026.png",
      name: "IAC 2026",
      date: "Oct 5–9, 2026",
      location: "Antalya, Turkey",
      url: "https://www.iac2026.org/",
      invert: true,
    },
    {
      logo: "/IMAGES/MEETS_LOGOS/Space Tech Expo Europe.png",
      name: "Space Tech Expo Europe",
      date: "Nov 17–19, 2026",
      location: "Bremen, Germany",
      url: "https://www.spacetechexpo-europe.com/whatson/",
      invert: true,
    },
  ];

  const logos = [
    { src: "/infinite-orbits.svg", alt: "infinite orbits", className: "h-8 md:h-10 lg:h-16" },
    { src: "/c3s.png", alt: "C3S", className: "h-8 md:h-12 lg:h-20" },
    { src: "/logo-u-space.svg", alt: "u-space", className: "h-8 md:h-10 lg:h-16" },
    { src: "/ESA_logo.png", alt: "ESA", className: "h-8 md:h-10 lg:h-16" },
  ];

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
      <h2
          className="text-4xl font-bold text-white text-center mb-16 tracking-tight uppercase"
          data-aos="fade-up"
          data-aos-offset="100"
          data-aos-easing="ease-in-sine"
          data-aos-duration="600"
      >
        {children}
      </h2>
  );

  return (
      <div>
        <Helmet>
          <title>Revolv Space</title>
          <meta name="description" content="We redefine small satellite capabilities through reliable and affordable space mechanisms and power systems." />
        </Helmet>

        <div className="relative flex flex-col h-full mt-20 overflow-x-hidden">

          {/* ── HERO ── */}
          <div className="grid md:grid-cols-2 z-40 min-h-[80vh] items-center">
            <div className="flex flex-col px-8 md:px-12 pt-24 md:pt-0">
              <h1 className="opacity-0 animate-fadeIn delay-0 pb-6 2xl:text-8xl xl:text-7xl lg:text-7xl text-4xl leading-none tracking-tighter uppercase font-bold text-white">
                Superpowers<br />for Satellites
              </h1>
              <p className="opacity-0 animate-fadeIn delay-1 uppercase text-white text-opacity-60 text-sm md:text-base lg:text-lg leading-relaxed tracking-tight mb-10 max-w-lg">
                We redefine small satellite capabilities through reliable and affordable space mechanisms and power systems
              </p>
              <GradientButtons
                  to1="/home/#products"
                  to2="/home/#meetrevolv"
                  description1="OUR PRODUCTS"
                  description2="MEET REVOLV"
              />
            </div>

            <div className="flex items-center justify-center md:justify-end py-16 md:py-0 md:pr-20">
              <div className="grid grid-cols-1 gap-10 text-white">
                <Counter type="custom" start={0} end={8} unit="" customLabel="global clients" upperText="" />
                <Counter type="custom" start={0} end={70} unit="" customLabel="units delivered" upperText="" />
                <Counter type="custom" start={0} end={27} unit="" customLabel="units launched" upperText="" />
              </div>
            </div>
          </div>

          {/* ── EXPERTISE ── */}
          <div className="px-6 md:px-12 py-24 md:py-32 z-40">
            <SectionTitle>Expertise</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-20 md:gap-12">
              {[
                {
                  src: "MARA_01_ISOMETRICA.png",
                  alt: "Space Mechanisms",
                  title: "Space Mechanisms",
                  desc: "We develop custom mechanisms compatible with the size and budget of small satellite missions, without compromising on reliability",
                },
                {
                  src: "PANELS_01_ISOMETRICA.png",
                  alt: "Solar Arrays",
                  title: "Solar Arrays",
                  desc: "We provide turnkey power generation systems, customized for your spacecraft and co-engineered with transparency together with your platform design team",
                },
              ].map((item, i) => (
                  <div
                      key={i}
                      className="group flex flex-col"
                      data-aos="fade-up"
                      data-aos-offset="100"
                      data-aos-delay={String(i * 150)}
                      data-aos-easing="ease-in-sine"
                      data-aos-duration="600"
                  >
                    <div className="w-full flex items-center justify-center mb-8 overflow-hidden">
                      <img
                          src={item.src}
                          alt={item.alt}
                          className="w-2/3 md:w-full max-h-72 object-contain transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="gradient-line w-0 group-hover:w-full transition-all duration-500 my-3" />
                    <h3 className="text-2xl md:text-3xl font-bold text-white uppercase mt-4 mb-3 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-white text-opacity-50 text-sm md:text-base uppercase leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
              ))}
            </div>
          </div>

          {/* ── PRODUCTS ── */}
          <div id="products" className="py-24 bg-black bg-opacity-40 z-0">
            <SectionTitle>Products</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mx-6 md:mx-40 xl:mx-56">
              {[
                { subheading: "The SADA — For Microsats", heading: "SARA", subtitle: "TRL 9", interactable: true, imageSrc: "/SARA.png", delay: 100 },
                { subheading: "The SADA — For SmallSats", heading: "MARA", subtitle: "TRL 6", interactable: true, imageSrc: "/MARA_1.png", delay: 200 },
                { subheading: "THE DEPLOYMENT HINGE", heading: "HOLLY", subtitle: "TRL 9", interactable: true, imageSrc: "/HOLLY.png", delay: 300 },
                { subheading: "THE SOLAR PANEL", heading: "PAVEL", subtitle: "TRL 9", interactable: true, imageSrc: "/PAVEL.png", delay: 400 },
                { subheading: "360° rot. inspection camera", heading: "KIMBALL", subtitle: "coming soon", interactable: true, imageSrc: "/KIMBALL.png", delay: 500 },


              ].map((p, i) => (
                  <div
                      key={i}
                      className="w-full"
                      data-aos="fade-up"
                      data-aos-offset="100"
                      data-aos-delay={String(p.delay)}
                      data-aos-easing="ease-in-sine"
                      data-aos-duration="500"
                  >
                    <ProductHome
                        subheading={p.subheading}
                        heading={p.heading}
                        subtitle={p.subtitle}
                        interactable={p.interactable}
                        imageSrc={p.imageSrc}
                    />
                  </div>
              ))}
            </div>
          </div>

          {/* ── TRUSTED BY ── */}
          <div className="py-20">
            <SectionTitle>Trusted By</SectionTitle>
            <div className="flex items-center justify-between mx-6 md:mx-40 xl:mx-56">
              {logos.map((logo, i) => (
                  <div key={i} className="flex items-center justify-center flex-1">
                    <img
                        className="h-14 md:h-16 lg:h-28 w-auto object-contain"
                        src={logo.src}
                        alt={logo.alt}
                    />
                  </div>
              ))}
            </div>
          </div>

          {/* ── LATEST NEWS ── */}
          <div className="px-6 md:px-16 py-20">
            <SectionTitle>Latest News</SectionTitle>
            <div className="flex flex-col divide-y divide-white divide-opacity-10">
              {latestNews.map((newsItem, index) => (
                  <div
                      key={index}
                      className="group cursor-pointer py-6 flex gap-5 items-center hover:bg-white hover:bg-opacity-5 transition-all duration-200 px-28"
                      data-aos="fade-up"
                      data-aos-offset="100"
                      data-aos-delay={String(index * 100)}
                      data-aos-easing="ease-in-sine"
                      data-aos-duration="500"
                      data-aos-once="true"
                      onClick={() => {
                        if (newsItem.link) window.open(newsItem.link, "_blank", "noopener,noreferrer");
                        else navigate(`/news/${newsItem.category}/${newsItem.news_id}`, { state: { NewItem: newsItem } });
                      }}
                  >
                    <img
                        src={"/IMAGES/" + newsItem.news_id + "/small.png"}
                        alt={newsItem.titleNews}
                        className="w-40 h-24 object-cover rounded-full flex-shrink-0"
                    />
                    <div className="flex flex-col gap-1">
                      <span
                          className="text-xs uppercase tracking-widest font-bold"
                          style={{
                            background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                      >
                        {newsItem.category} · {newsItem.publication_date}
                      </span>
                      <h3 className="text-white text-sm md:text-base font-bold uppercase leading-snug line-clamp-2 group-hover:opacity-70 transition-opacity">
                        {newsItem.titleNews?.replace(/\\n/g, " ")}
                      </h3>
                      <p className="text-white text-opacity-40 text-xs uppercase line-clamp-2 leading-relaxed">
                        {newsItem.subtitle?.replace(/\\n/g, " ")}
                      </p>
                    </div>
                  </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center mt-10 py-4">
            <Link to="/news">
              <button
                  className="rounded-full  h-12 text-center text-white px-8 font-bold py-2 transform transition-transform duration-200 hover:scale-110"
                  style={{ background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" }}
              >
                ALL NEWS
              </button>
            </Link>
          </div>
        </div>

        {/* ── MEET REVOLV ── */}
        <div id="meetrevolv" className="py-20">
          <SectionTitle>Meet Revolv</SectionTitle>
          <div className="flex flex-col px-4 md:px-32">
            {events.map((event, index) => (
                <Link key={index} to={event.url} target="_blank" rel="noopener noreferrer" className="group">
                  <div className="flex flex-row items-center gap-2 md:gap-4 py-5 px-3 border-b border-white border-opacity-10 transition-all duration-200 group-hover:bg-white group-hover:bg-opacity-5">

                    {/* Numero */}
                    <div className="w-5 flex-shrink-0 text-white opacity-20 text-xs ">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    {/* Logo */}
                    <div className="w-16 md:w-36 flex-shrink-0">
                      <img
                          className="w-full h-22 md:h-22 object-contain object-left"
                          style={{ filter: event.invert ? "brightness(0) invert(1)" : "none" }}
                          src={event.logo}
                          alt={event.name}
                      />
                    </div>

                    {/* Nome — solo desktop */}
                    <div className="flex-1 hidden md:block uppercase text-white font-bold tracking-wide text-sm">
                      {event.name}
                    </div>

                    {/* Location — solo desktop */}
                    <div className="flex-1 hidden md:block text-white opacity-40 text-sm uppercase tracking-wide">
                      {event.location}
                    </div>

                    {/* Data + location mobile */}
                    <div className="ml-auto text-right flex-shrink-0">
                      <div className="text-white text-xs md:text-base tracking-wide whitespace-nowrap">
                        {event.date}
                      </div>
                      <div className="text-white opacity-40 text-xs uppercase tracking-wide md:hidden whitespace-nowrap">
                        {event.location}
                      </div>
                    </div>

                    {/* Freccia */}
                    <div className="text-white opacity-20 group-hover:opacity-100 transition-opacity duration-200 ml-1 flex-shrink-0 text-sm">
                      →
                    </div>

                  </div>
                </Link>
            ))}
          </div>
        </div>

        {/* ── INTEGRATORS & OPERATORS ── */}
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="/two_planet.png" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black bg-opacity-70" />
          </div>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2">
            {[
              {
                title: "Satellite\nIntegrators",
                text: "Gone are the days when employing mechanisms on small satellites was a daunting challenge. We're rewriting the rules.\n\nOur actuation systems boost the performance of your small satellites and the capabilities that you can offer to your customers.",
                border: "md:border-r border-white border-opacity-10",
              },
              {
                title: "Satellite\nOperators",
                text: "Forget about low uptimes and satellite unavailability due to platform limitations.\n\nOur high-performance devices elevate your satellite's potential, boosting the duty cycles of your platforms, your mission return and your revenues.",
                border: "",
              },
            ].map((block, i) => (
                <div
                    key={i}
                    className={`group flex flex-col justify-between px-8 md:px-20 py-16 md:py-28 border-b border-white border-opacity-10 md:border-b-0 ${block.border} transition-all duration-300 hover:bg-white hover:bg-opacity-5`}
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white opacity-30 mb-4">For</p>
                    <h2 className="text-4xl md:text-6xl xl:text-7xl font-bold uppercase text-white leading-none mb-8 whitespace-pre-line">
                      {block.title}
                    </h2>
                    <div className="gradient-line w-0 group-hover:w-16 transition-all duration-500 mb-8" />
                    <p className="text-sm md:text-base uppercase leading-loose opacity-60 text-white max-w-md whitespace-pre-line">
                      {block.text}
                    </p>
                  </div>
                  <Link to="/contact" className="mt-10 self-start">
                    <button
                        className="rounded-full text-white font-bold py-3 px-8 text-sm uppercase tracking-widest transform transition-transform duration-200 hover:scale-110"
                        style={{ background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" }}
                    >
                      Find Out How
                    </button>
                  </Link>
                </div>
            ))}
          </div>
        </div>

        {/* ── CONTACT CTA ── */}
        <div className="flex items-center justify-center py-20">
          <Link to="/contact">
            <button
                className="rounded-full h-12 text-center text-white px-8 font-bold py-3 transform transition-transform duration-200 hover:scale-110 text-sm uppercase tracking-widest"
                style={{ background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" }}
            >
              Contact Us
            </button>
          </Link>
        </div>

      </div>

);
}